'use client';

import React from 'react';
import { CheckIcon } from '@milanote/roma-icons';
import { cx } from '../../../utils/cx';
import { KeyboardShortcut } from '../../base/keyboardShortcut';
import { Menu } from '../../base/menu';
import type { MenuSize } from '../../base/menu';
import { Popover } from '../../base/popover';
import type { MenuNode } from './contextMenuTypes';

const bodyStylesBySize: Record<MenuSize, string> = {
    xs: '',
    sm: '',
    md: '',
    lg: 'py-xl [--roma-popover-body-padding-x:var(--spacing-lg)]',
};

/** Renders one resolved menu node, recursively composing submenus when needed. */
const ResolvedContextMenuNode: React.FC<{
    node: MenuNode;
    size: MenuSize;
    showIcons: boolean;
    showShortcuts: boolean;
}> = ({ node, size, showIcons, showShortcuts }) => {
    if (node.type === 'divider') return <Menu.Separator />;

    if (node.customContent) {
        return (
            <Menu.CustomItem id={node.id} textValue={node.label} onAction={node.onAction} isDisabled={node.isDisabled}>
                {node.customContent}
            </Menu.CustomItem>
        );
    }

    const shortcut = node.shortcut ? <KeyboardShortcut.Group parts={node.shortcut} preferSymbol /> : undefined;
    const trailing = node.isSelected ? (
        <CheckIcon aria-hidden="true" className="size-4" />
    ) : showShortcuts ? (
        shortcut
    ) : undefined;
    const item = (
        <Menu.Item
            id={node.id}
            label={node.label}
            leading={showIcons ? node.leading : undefined}
            trailing={trailing}
            onAction={node.onAction}
            isDisabled={node.isDisabled}
        />
    );

    if (!node.children) return item;

    return (
        <Menu.Submenu>
            {item}
            <Menu.SubmenuPopover>
                <Popover.Body className={bodyStylesBySize[size]}>
                    <Menu aria-label={node.label} size={size}>
                        {node.children.map((child) => (
                            <ResolvedContextMenuNode
                                key={`${child.type}:${child.id}`}
                                node={child}
                                size={size}
                                showIcons={showIcons}
                                showShortcuts={showShortcuts}
                            />
                        ))}
                    </Menu>
                </Popover.Body>
            </Menu.SubmenuPopover>
        </Menu.Submenu>
    );
};

export type ContextMenuContentProps = {
    'aria-label': string;
    nodes: MenuNode[];
    autoFocus?: boolean;
    size?: MenuSize;
    className?: string;
    showIcons?: boolean;
    showShortcuts?: boolean;
};

export type ContextMenuProps = ContextMenuContentProps;

/** Renders resolved context-menu nodes inside an owning popover surface. */
const ContextMenuContent: React.FC<ContextMenuContentProps> = ({
    nodes,
    autoFocus,
    size = 'xs',
    className,
    showIcons = false,
    showShortcuts = true,
    ...props
}) => (
    <Popover.Body className={cx(bodyStylesBySize[size], className)}>
        <Menu aria-label={props['aria-label']} autoFocus={autoFocus} size={size}>
            {nodes.map((node) => (
                <ResolvedContextMenuNode
                    key={`${node.type}:${node.id}`}
                    node={node}
                    size={size}
                    showIcons={showIcons}
                    showShortcuts={showShortcuts}
                />
            ))}
        </Menu>
    </Popover.Body>
);

/** Renders resolved application context-menu nodes on a static Roma popover surface. */
const ContextMenuShell: React.FC<ContextMenuProps> = ({ className = 'w-72', size = 'xs', ...props }) => (
    <Popover.Base className={className}>
        <ContextMenuContent {...props} size={size} />
    </Popover.Base>
);

export const ContextMenu = Object.assign(ContextMenuShell, { Content: ContextMenuContent });
